import { LitElement, html, css } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { createContext, provide } from "@lit/context";

export interface ThemeConfig {
  light: ThemeModeConfig;
  dark: ThemeModeConfig;
}
export type ThemePreference = "light" | "dark" | "system";
export type PartialTheme = Partial<ThemeConfig>;

/** Extended ThemeModeConfig: added typography & spacing tokens */
export interface ThemeModeConfig {
  glass: {
    baseColor: string;
    /** Optional per-level base shades; if omitted, secondary/tertiary are derived by mixing baseColor with foreground */
    baseShades?: {
      primary?: string;
      secondary?: string;
      tertiary?: string;
    };
    opacities: Record<"primary" | "secondary" | "tertiary" | "solid", number>;
  };
  glassHover: {
    color: string;
    opacity: Record<"primary" | "secondary" | "tertiary" | "solid", number>;
  };
  overlay: {
    color: string;
    opacities: Record<"primary" | "secondary" | "tertiary" | "solid", number>;
  };

  fontFamily: string;
  /** Monospace font stack; used for --sky-font-mono and bounded mono presets */
  fontFamilyMono?: string;

  /* typography scales & spacing */
  fontSize?: Record<
    "xs" | "sm" | "md" | "lg" | "xl" | "2xl" | "3xl" | "4xl" | "5xl" | "6xl",
    string
  >;
  fontWeight?: Record<
    "thin" | "light" | "normal" | "medium" | "semibold" | "bold" | "extrabold",
    string
  >;
  lineHeight?: Record<
    "tight" | "snug" | "normal" | "relaxed" | "loose",
    string
  >;
  letterSpacing?: Record<
    "tighter" | "tight" | "normal" | "wide" | "wider",
    string
  >;
  glassSurface?: {
    top: number;
    bottom: number;
  };
  spacing?: Record<
    | "0"
    | "px"
    | "0.5"
    | "1"
    | "1.5"
    | "2"
    | "2.5"
    | "3"
    | "3.5"
    | "4"
    | "5"
    | "6"
    | "8"
    | "10"
    | "12"
    | "16"
    | "20"
    | "24"
    | "32"
    | "40"
    | "48"
    | "56"
    | "64",
    string
  >;

  /* Simplified color system - only primaries needed */
  color: {
    /* Only primary colors needed - everything else auto-generated */
    background: string;
    foreground: string;
    active: string;
    success: string;
    warning: string;
    danger: string;
    info: string;
  };

  /* Existing properties */
  blur: Record<"primary" | "secondary" | "tertiary", string>;
  radius: Record<"primary" | "secondary" | "tertiary" | "full", string>;
  boxshadow: Record<
    "primary" | "secondary" | "tertiary" | "inset" | "hover",
    string
  >;
  brightness: Record<"primary" | "secondary" | "tertiary", string>;
  saturation: Record<"primary" | "secondary" | "tertiary", string>;
  border: Record<"primary" | "secondary" | "tertiary", string>;
  separator: string;

  /** Motion: duration and easing for consistent, themeable transitions */
  motion?: {
    duration?: Record<"instant" | "fast" | "normal" | "slow", string>;
    ease?: Record<"default" | "in" | "out" | "inOut" | "bounce", string>;
  };
  /** Z-index scale for predictable stacking (dropdown, modal, toast, etc.) */
  zIndex?: Record<
    "base" | "dropdown" | "sticky" | "fixed" | "modal" | "popover" | "toast" | "max",
    number
  >;
  /** Opacity tokens for disabled/muted states */
  opacity?: Record<"disabled" | "muted" | "subtle", number>;
  /** Optional native scrollbar color tints; sizing stays platform-native. */
  scrollbar?: {
    thumb?: string;
    thumbHover?: string;
    track?: string;
  };
}

const THEME_KEY = "theme";

/* default theme: Pure Glass (sky-ui-example DEFAULT_SITE_THEME_ID) */
export const defaultTheme: ThemeConfig = {
  light: {
    glass: {
      baseColor: "#FFFFFF",
      baseShades: {
        secondary: "#F1F5F9",
        tertiary: "#E2E8F0",
      },
      /* Ultra-thin frosted fill — hierarchy via shade + blur */
      opacities: { primary: 0.1, secondary: 0.12, tertiary: 0.16, solid: 0.18 },
    },
    overlay: {
      color: "#E8EEF6",
      opacities: { primary: 0.75, secondary: 0.33, tertiary: 0.19, solid: 1.0 },
    },
    glassHover: {
      color: "#0F172A",
      opacity: { primary: 0.06, secondary: 0.08, tertiary: 0.1, solid: 1.0 },
    },
    fontFamily:
      "'Inter', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    fontFamilyMono:
      "'JetBrains Mono', 'SF Mono', ui-monospace, Consolas, monospace",
    fontSize: {
      xs: "0.75rem",
      sm: "0.875rem",
      md: "1rem",
      lg: "1.125rem",
      xl: "1.25rem",
      "2xl": "1.5rem",
      "3xl": "1.875rem",
      "4xl": "2.25rem",
      "5xl": "3rem",
      "6xl": "3.75rem",
    },
    fontWeight: {
      thin: "100",
      light: "300",
      normal: "400",
      medium: "500",
      semibold: "600",
      bold: "700",
      extrabold: "800",
    },
    lineHeight: {
      tight: "1.1",
      snug: "1.25",
      normal: "1.5",
      relaxed: "1.75",
      loose: "2",
    },
    letterSpacing: {
      tighter: "-0.03em",
      tight: "-0.015em",
      normal: "0em",
      wide: "0.02em",
      wider: "0.04em",
    },
    spacing: {
      "0": "0px",
      px: "1px",
      "0.5": "0.125rem",
      "1": "0.25rem",
      "1.5": "0.375rem",
      "2": "0.5rem",
      "2.5": "0.625rem",
      "3": "0.75rem",
      "3.5": "0.875rem",
      "4": "1rem",
      "5": "1.25rem",
      "6": "1.5rem",
      "8": "2rem",
      "10": "2.5rem",
      "12": "3rem",
      "16": "4rem",
      "20": "5rem",
      "24": "6rem",
      "32": "8rem",
      "40": "10rem",
      "48": "12rem",
      "56": "14rem",
      "64": "16rem",
    },

    color: {
      background: "#E8EEF6",
      foreground: "#0F172A",
      active: "#2563EB",
      success: "#17C964",
      warning: "#F5A524",
      danger: "#FF383C",
      info: "#6366F1",
    },

    blur: { primary: "12px", secondary: "24px", tertiary: "32px" },
    radius: {
      primary: "8px",
      secondary: "12px",
      tertiary: "16px",
      full: "9999px",
    },
    boxshadow: {
      primary: "0 1px 2px rgba(15, 23, 42, 0.04), 0 2px 8px rgba(15, 23, 42, 0.04)",
      secondary: "0 2px 4px rgba(15, 23, 42, 0.04), 0 4px 12px rgba(15, 23, 42, 0.05)",
      tertiary: "0 4px 8px rgba(15, 23, 42, 0.05), 0 8px 20px rgba(15, 23, 42, 0.06)",
      inset:
        "inset 0 1px 0 rgba(255, 255, 255, 0.55), inset 0 -1px 0 rgba(15, 23, 42, 0.03)",
      hover: "0 2px 6px rgba(15, 23, 42, 0.06), 0 6px 16px rgba(15, 23, 42, 0.07)",
    },
    brightness: { primary: "1.04", secondary: "1.08", tertiary: "1.10" },
    saturation: { primary: "1.08", secondary: "1.14", tertiary: "1.20" },
    separator: "rgba(15, 23, 42, 0.12)",
    border: {
      primary: "1px solid rgba(15, 23, 42, 0.14)",
      secondary:
        "1px solid color-mix(in srgb, rgba(15, 23, 42, 0.14) 72%, transparent)",
      tertiary:
        "1px solid color-mix(in srgb, rgba(15, 23, 42, 0.14) 52%, transparent)",
    },
    glassSurface: { top: 0.14, bottom: 0.03 },
    motion: {
      duration: { instant: "0ms", fast: "120ms", normal: "200ms", slow: "300ms" },
      ease: {
        default: "cubic-bezier(0.4, 0, 0.2, 1)",
        in: "cubic-bezier(0.4, 0, 1, 1)",
        out: "cubic-bezier(0, 0, 0.2, 1)",
        inOut: "cubic-bezier(0.4, 0, 0.2, 1)",
        bounce: "cubic-bezier(0.34, 1.56, 0.64, 1)",
      },
    },
    zIndex: {
      base: 0,
      dropdown: 1000,
      sticky: 1100,
      fixed: 1200,
      modal: 1300,
      popover: 1400,
      toast: 1500,
      max: 9999,
    },
    opacity: { disabled: 0.5, muted: 0.7, subtle: 0.85 },
    scrollbar: {
      track: "transparent",
      thumb:
        "color-mix(in oklch, color-mix(in oklch, #2563EB 36%, #0F172A 20%), transparent)",
      thumbHover:
        "color-mix(in oklch, color-mix(in oklch, #2563EB 54%, #0F172A 16%), transparent)",
    },
  },

  dark: {
    glass: {
      baseColor: "#141B2D",
      baseShades: {
        secondary: "#1A2235",
        tertiary: "#222C42",
      },
      opacities: { primary: 0.16, secondary: 0.18, tertiary: 0.22, solid: 0.24 },
    },
    overlay: {
      color: "#0B1020",
      opacities: { primary: 0.75, secondary: 0.33, tertiary: 0.19, solid: 1.0 },
    },
    glassHover: {
      color: "#E2E8F0",
      opacity: { primary: 0.08, secondary: 0.1, tertiary: 0.12, solid: 1.0 },
    },
    fontFamily:
      "'Inter', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    fontFamilyMono:
      "'JetBrains Mono', 'SF Mono', ui-monospace, Consolas, monospace",
    fontSize: {
      xs: "0.75rem",
      sm: "0.875rem",
      md: "1rem",
      lg: "1.125rem",
      xl: "1.25rem",
      "2xl": "1.5rem",
      "3xl": "1.875rem",
      "4xl": "2.25rem",
      "5xl": "3rem",
      "6xl": "3.75rem",
    },
    fontWeight: {
      thin: "100",
      light: "300",
      normal: "400",
      medium: "500",
      semibold: "600",
      bold: "700",
      extrabold: "800",
    },
    lineHeight: {
      tight: "1.1",
      snug: "1.25",
      normal: "1.5",
      relaxed: "1.75",
      loose: "2",
    },
    letterSpacing: {
      tighter: "-0.03em",
      tight: "-0.015em",
      normal: "0em",
      wide: "0.02em",
      wider: "0.04em",
    },
    spacing: {
      "0": "0px",
      px: "1px",
      "0.5": "0.125rem",
      "1": "0.25rem",
      "1.5": "0.375rem",
      "2": "0.5rem",
      "2.5": "0.625rem",
      "3": "0.75rem",
      "3.5": "0.875rem",
      "4": "1rem",
      "5": "1.25rem",
      "6": "1.5rem",
      "8": "2rem",
      "10": "2.5rem",
      "12": "3rem",
      "16": "4rem",
      "20": "5rem",
      "24": "6rem",
      "32": "8rem",
      "40": "10rem",
      "48": "12rem",
      "56": "14rem",
      "64": "16rem",
    },

    color: {
      background: "#0B1020",
      foreground: "#E2E8F0",
      active: "#2563EB",
      success: "#17C964",
      warning: "#F7B750",
      danger: "#DB3B3E",
      info: "#818CF8",
    },

    blur: { primary: "12px", secondary: "24px", tertiary: "32px" },
    radius: {
      primary: "8px",
      secondary: "12px",
      tertiary: "16px",
      full: "9999px",
    },
    boxshadow: {
      primary: "0 1px 2px rgba(0, 0, 0, 0.2), 0 2px 8px rgba(0, 0, 0, 0.22)",
      secondary: "0 2px 4px rgba(0, 0, 0, 0.22), 0 4px 14px rgba(0, 0, 0, 0.26)",
      tertiary: "0 4px 8px rgba(0, 0, 0, 0.24), 0 8px 20px rgba(0, 0, 0, 0.3)",
      inset:
        "inset 0 1px 0 rgba(255, 255, 255, 0.1), inset 0 -1px 0 rgba(0, 0, 0, 0.18)",
      hover: "0 2px 6px rgba(0, 0, 0, 0.26), 0 6px 16px rgba(0, 0, 0, 0.32)",
    },
    brightness: { primary: "1.04", secondary: "1.08", tertiary: "1.10" },
    saturation: { primary: "1.08", secondary: "1.14", tertiary: "1.20" },
    separator: "rgba(226, 232, 240, 0.1)",
    border: {
      primary: "1px solid rgba(226, 232, 240, 0.14)",
      secondary:
        "1px solid color-mix(in srgb, rgba(226, 232, 240, 0.14) 72%, transparent)",
      tertiary:
        "1px solid color-mix(in srgb, rgba(226, 232, 240, 0.14) 52%, transparent)",
    },
    glassSurface: { top: 0.16, bottom: 0.05 },
    motion: {
      duration: { instant: "0ms", fast: "120ms", normal: "200ms", slow: "300ms" },
      ease: {
        default: "cubic-bezier(0.4, 0, 0.2, 1)",
        in: "cubic-bezier(0.4, 0, 1, 1)",
        out: "cubic-bezier(0, 0, 0.2, 1)",
        inOut: "cubic-bezier(0.4, 0, 0.2, 1)",
        bounce: "cubic-bezier(0.34, 1.56, 0.64, 1)",
      },
    },
    zIndex: {
      base: 0,
      dropdown: 1000,
      sticky: 1100,
      fixed: 1200,
      modal: 1300,
      popover: 1400,
      toast: 1500,
      max: 9999,
    },
    opacity: { disabled: 0.5, muted: 0.7, subtle: 0.85 },
    scrollbar: {
      track: "transparent",
      thumb:
        "color-mix(in oklch, color-mix(in oklch, #2563EB 44%, #E2E8F0 16%), transparent)",
      thumbHover:
        "color-mix(in oklch, color-mix(in oklch, #2563EB 62%, #E2E8F0 12%), transparent)",
    },
  },
};

export function getThemePreference(): ThemePreference {
  try {
    if (typeof localStorage !== "undefined") {
      const v = localStorage.getItem(THEME_KEY) as ThemePreference | null;
      return v === "light" || v === "dark" || v === "system" ? v : "system";
    }
  } catch { }
  return "system";
}

export function resolveActualTheme(pref: ThemePreference): "light" | "dark" {
  if (typeof window === "undefined") return "light";
  if (pref === "system") {
    try {
      if (window.matchMedia) {
        return window.matchMedia("(prefers-color-scheme: dark)").matches
          ? "dark"
          : "light";
      }
    } catch { }
    return "light";
  }
  return pref;
}

/** Returns "light" | "dark" (prefers provider, falls back to system). SSR-safe: returns "light" when document is undefined. */
export function getCurrentTheme(): "light" | "dark" {
  if (typeof document === "undefined") return "light";
  const de = document.documentElement;
  const v = de.getAttribute("data-theme");
  if (v === "light" || v === "dark") return v;
  return typeof matchMedia !== "undefined" && matchMedia("(prefers-color-scheme: dark)").matches
    ? "dark"
    : "light";
}

/** Subscribe to theme changes. Returns an unsubscribe function. SSR-safe: returns no-op when window is undefined. */
export function onThemeChange(
  cb: (mode: "light" | "dark", preference?: ThemePreference) => void,
) {
  if (typeof window === "undefined") return () => {};
  const handler = (e: Event) => {
    const { mode, preference } = (e as CustomEvent).detail || {};
    if (mode === "light" || mode === "dark") cb(mode, preference);
    else cb(getCurrentTheme(), undefined);
  };
  window.addEventListener("sky-theme", handler);

  queueMicrotask(() => cb(getCurrentTheme(), undefined));
  return () => window.removeEventListener("sky-theme", handler);
}

function deepMerge<T>(target: T, source: Partial<T>): T {
  const t: any = Array.isArray(target)
    ? [...(target as any)]
    : { ...(target as any) };
  for (const k in source) {
    const sv: any = (source as any)[k];
    if (sv && typeof sv === "object" && !Array.isArray(sv)) {
      t[k] = deepMerge((target as any)[k], sv);
    } else {
      t[k] = sv ?? (target as any)[k];
    }
  }
  return t;
}

let lastMergeUser: Partial<ThemeConfig> | undefined;
let lastMergeResult: ThemeConfig | undefined;

export function mergeTheme(userTheme: Partial<ThemeConfig>): ThemeConfig {
  if (userTheme === lastMergeUser && lastMergeResult) return lastMergeResult;
  lastMergeResult = {
    light: deepMerge(defaultTheme.light, userTheme.light ?? {}),
    dark: deepMerge(defaultTheme.dark, userTheme.dark ?? {}),
  };
  lastMergeUser = userTheme;
  return lastMergeResult;
}

/* color & glass helpers */

function clampAlpha(alpha: number | undefined, fallback = 1): number {
  const value =
    typeof alpha === "number" && Number.isFinite(alpha) ? alpha : fallback;

  return Math.max(0, Math.min(1, value));
}

function clampPercent(value: number): number {
  if (!Number.isFinite(value)) return 100;
  return Math.max(0, Math.min(100, Math.round(value)));
}

function isOKLCH(value: string): boolean {
  return /^oklch\(\s*[\d.]+\s+[\d.]+\s+[\d.]+(?:deg)?(?:\s*\/\s*[\d.]+%?)?\s*\)$/i.test(
    value.trim(),
  );
}

function toOKLCH(hexOrOKLCH: string): string {
  if (isOKLCH(hexOrOKLCH)) return hexOrOKLCH;
  return hexOrOKLCH;
}

function toOKLCHAlpha(hexOrOKLCH: string, alpha: number): string {
  const safeAlpha = clampAlpha(alpha);
  const alphaPct = clampPercent(safeAlpha * 100);

  if (safeAlpha <= 0) return "transparent";
  if (safeAlpha >= 1) return toOKLCH(hexOrOKLCH);

  if (isOKLCH(hexOrOKLCH)) {
    const value = hexOrOKLCH.trim();

    return value.includes("/")
      ? value
      : value.replace(/\)$/, ` / ${alphaPct}%)`);
  }

  return `color-mix(in oklch, ${hexOrOKLCH} ${alphaPct}%, transparent)`;
}

function mixOKLCH(colorA: string, amountA: number, colorB: string): string {
  return `color-mix(in oklch, ${colorA} ${clampPercent(amountA)}%, ${colorB})`;
}

/**
 * generateVarMap emits typography & spacing tokens and semantic role tokens.
 *
 * Glass hierarchy is shade-based with optional per-tier fill opacity:
 * - primary   = base/default glass shade
 * - secondary = raised/elevated glass shade
 * - tertiary  = floating/modal/popover glass shade
 * - Fill strength: glass.opacities.primary | secondary | tertiary
 *   (secondary/tertiary fall back to primary when omitted).
 */
function generateVarMap(
  theme: ThemeModeConfig,
  isDark: boolean,
): Record<string, string> {
  const out: Record<string, string> = {};

  const set = (k: string, v?: string) => {
    if (v != null) out[`--${k}`] = v;
  };

  /* base contrast tokens */

  set("sky-text-dark", toOKLCH(isDark ? "#FFFFFF" : "#0B1020"));
  set("sky-text-light", toOKLCH(isDark ? "#030712" : "#FFFFFF"));

  /* font family tokens */

  const fontSans = theme.fontFamily;
  const fontMono = theme.fontFamilyMono ?? "ui-monospace, monospace";

  set("sky-font", fontSans);
  set("sky-font-sans", fontSans);
  set("sky-font-mono", fontMono);

  /* background + text tokens */

  set("sky-background-color", toOKLCH(theme.color.background));
  set("sky-text-primary", toOKLCH(theme.color.foreground));

  const textSteps: Record<
    "secondary" | "tertiary" | "quaternary" | "quinary",
    number
  > = isDark
    ? {
        secondary: 82,
        tertiary: 66,
        quaternary: 50,
        quinary: 36,
      }
    : {
        secondary: 76,
        tertiary: 60,
        quaternary: 44,
        quinary: 30,
      };

  Object.entries(textSteps).forEach(([level, pct]) => {
    set(
      `sky-text-${level}`,
      `color-mix(in oklch, var(--sky-text-primary) ${pct}%, var(--sky-background-color))`,
    );
  });

  ["primary", "secondary", "tertiary", "quaternary", "quinary"].forEach(
    (level) => {
      set(
        `sky-text-${level}-hover`,
        `color-mix(in oklch, var(--sky-text-${level}) 86%, var(--sky-text-dark))`,
      );
    },
  );

  /* shade-based glass generation */

  const { baseColor, baseShades, opacities: op } = theme.glass;

  const glassShadeAnchor = theme.color.foreground;

  const glassShadeSteps = isDark
    ? {
        primary: 96,
        secondary: 88,
        tertiary: 78,
      }
    : {
        primary: 96,
        secondary: 90,
        tertiary: 82,
      };

  const glassPrimaryShade =
  baseShades?.primary ??
  mixOKLCH(
    baseColor,
    glassShadeSteps.primary,
    glassShadeAnchor,
  );

  const glassSecondaryShade =
    baseShades?.secondary ??
    mixOKLCH(baseColor, glassShadeSteps.secondary, glassShadeAnchor);

  const glassTertiaryShade =
    baseShades?.tertiary ??
    mixOKLCH(baseColor, glassShadeSteps.tertiary, glassShadeAnchor);

  const glassStrengthFallback = isDark ? 0.4 : 0.34;
  const glassStrength = {
    primary: clampAlpha(op.primary, glassStrengthFallback),
    secondary: clampAlpha(
      op.secondary ?? op.primary,
      glassStrengthFallback
    ),
    tertiary: clampAlpha(op.tertiary ?? op.primary, glassStrengthFallback),
    // Solid surface fill (inputs / actions). Defaults fully opaque, but themes
    // can lower `opacities.solid` for a translucent "frosted solid" look.
    solid: clampAlpha(op.solid, 1),
  };

  set(
    "sky-glass-primary",
    toOKLCHAlpha(glassPrimaryShade, glassStrength.primary)
  );
  set(
    "sky-glass-secondary",
    toOKLCHAlpha(glassSecondaryShade, glassStrength.secondary)
  );
  set(
    "sky-glass-tertiary",
    toOKLCHAlpha(glassTertiaryShade, glassStrength.tertiary)
  );

  set("sky-glass-solid", toOKLCHAlpha(baseColor, glassStrength.solid));

  const glassSurfaceTopAlpha = clampAlpha(
    theme.glassSurface?.top,
    isDark ? 0.26 : 0.24,
  );

  const glassSurfaceBottomAlpha = clampAlpha(
    theme.glassSurface?.bottom,
    isDark ? 0.08 : 0.06,
  );

  set(
    "sky-glass-surface",
    `linear-gradient(
      180deg,
      ${toOKLCHAlpha(glassSecondaryShade, glassSurfaceTopAlpha)},
      ${toOKLCHAlpha(glassPrimaryShade, glassSurfaceBottomAlpha)}
    )`,
  );

  /* overlay tokens */

  Object.entries(theme.overlay.opacities).forEach(([k, v]) => {
    set(`sky-overlay-${k}`, toOKLCHAlpha(theme.overlay.color, v));
  });

  /* hover overlay tokens */

  Object.entries(theme.glassHover.opacity).forEach(([k, v]) => {
    set(`sky-hover-${k}`, toOKLCHAlpha(theme.glassHover.color, v));
  });

  /* border / radius / blur / effects */

  Object.entries(theme.border).forEach(([k, v]) => {
    set(`sky-border-${k}`, v);
  });

  Object.entries(theme.radius).forEach(([k, v]) => {
    set(`sky-radius-${k}`, v);
  });

  Object.entries(theme.blur).forEach(([k, v]) => {
    set(`sky-blur-${k}`, `blur(${v})`);
  });

  Object.entries(theme.brightness).forEach(([k, v]) => {
    set(`sky-brightness-${k}`, `brightness(${v})`);
  });

  Object.entries(theme.saturation).forEach(([k, v]) => {
    set(`sky-saturation-${k}`, `saturate(${v})`);
  });

  Object.entries(theme.boxshadow).forEach(([k, v]) => {
    set(`sky-box-shadow-${k}`, v);
  });

  set("sky-border-light", theme.separator);

  /* motion tokens */

  Object.entries(theme.motion?.duration ?? {}).forEach(([k, v]) => {
    set(`sky-duration-${k}`, v);
  });

  Object.entries(theme.motion?.ease ?? {}).forEach(([k, v]) => {
    set(`sky-ease-${k}`, v);
  });

  set(
    "sky-transition-default",
    [
      "background-color var(--sky-duration-normal, 200ms) var(--sky-ease-default, cubic-bezier(0.4, 0, 0.2, 1))",
      "border-color var(--sky-duration-normal, 200ms) var(--sky-ease-default, cubic-bezier(0.4, 0, 0.2, 1))",
      "color var(--sky-duration-normal, 200ms) var(--sky-ease-default, cubic-bezier(0.4, 0, 0.2, 1))",
      "box-shadow var(--sky-duration-normal, 200ms) var(--sky-ease-default, cubic-bezier(0.4, 0, 0.2, 1))",
      "transform var(--sky-duration-normal, 200ms) var(--sky-ease-default, cubic-bezier(0.4, 0, 0.2, 1))",
      "opacity var(--sky-duration-normal, 200ms) var(--sky-ease-default, cubic-bezier(0.4, 0, 0.2, 1))",
    ].join(", "),
  );

  set(
    "sky-transition-fast",
    [
      "background-color var(--sky-duration-fast, 120ms) var(--sky-ease-default, cubic-bezier(0.4, 0, 0.2, 1))",
      "border-color var(--sky-duration-fast, 120ms) var(--sky-ease-default, cubic-bezier(0.4, 0, 0.2, 1))",
      "color var(--sky-duration-fast, 120ms) var(--sky-ease-default, cubic-bezier(0.4, 0, 0.2, 1))",
      "box-shadow var(--sky-duration-fast, 120ms) var(--sky-ease-default, cubic-bezier(0.4, 0, 0.2, 1))",
      "opacity var(--sky-duration-fast, 120ms) var(--sky-ease-default, cubic-bezier(0.4, 0, 0.2, 1))",
    ].join(", "),
  );

  /* z-index tokens */

  Object.entries(theme.zIndex ?? {}).forEach(([k, v]) => {
    set(`sky-z-${k}`, String(v));
  });

  /* opacity tokens */

  Object.entries(theme.opacity ?? {}).forEach(([k, v]) => {
    set(`sky-opacity-${k}`, String(v));
  });

  /* native scrollbar color tokens (size stays OS-native) */

  const scrollbar = theme.scrollbar;
  set("sky-scrollbar-track", scrollbar?.track ?? "transparent");
  set(
    "sky-scrollbar-thumb",
    scrollbar?.thumb ??
      (isDark
        ? "color-mix(in oklch, var(--sky-text-tertiary) 78%, transparent)"
        : "color-mix(in oklch, var(--sky-text-tertiary) 68%, transparent)"),
  );
  set(
    "sky-scrollbar-thumb-hover",
    scrollbar?.thumbHover ??
      (isDark
        ? "color-mix(in oklch, var(--sky-text-secondary) 52%, var(--sky-active-primary))"
        : "color-mix(in oklch, var(--sky-text-secondary) 44%, var(--sky-active-primary))"),
  );

  /* text shadow */

  set(
    "sky-text-shadow-on-glass",
    isDark
      ? "0 1px 2px rgba(0,0,0,0.45)"
      : "0 1px 1px rgba(255,255,255,0.55)",
  );

  /* semantic color scales */

  const semanticColors = [
    "active",
    "success",
    "warning",
    "danger",
    "info",
  ] as const;

  semanticColors.forEach((key) => {
    const baseColor = theme.color[key];

    set(`sky-${key}-primary`, baseColor);

    set(
      `sky-${key}-secondary`,
      `color-mix(in oklch, var(--sky-${key}-primary) 82%, var(--sky-background-color))`,
    );

    set(
      `sky-${key}-tertiary`,
      `color-mix(in oklch, var(--sky-${key}-primary) 62%, var(--sky-background-color))`,
    );

    set(
      `sky-${key}-quaternary`,
      `color-mix(in oklch, var(--sky-${key}-primary) 42%, var(--sky-background-color))`,
    );

    set(
      `sky-${key}-quinary`,
      `color-mix(in oklch, var(--sky-${key}-primary) 24%, var(--sky-background-color))`,
    );

    set(
      `sky-${key}-primary-dark`,
      `color-mix(in oklch, var(--sky-${key}-primary) 88%, var(--sky-text-dark))`,
    );

    set(
      `sky-${key}-secondary-dark`,
      `color-mix(in oklch, var(--sky-${key}-primary) 72%, var(--sky-text-dark))`,
    );

    set(
      `sky-${key}-tertiary-dark`,
      `color-mix(in oklch, var(--sky-${key}-primary) 56%, var(--sky-text-dark))`,
    );

    set(
      `sky-${key}-quaternary-dark`,
      `color-mix(in oklch, var(--sky-${key}-primary) 40%, var(--sky-text-dark))`,
    );

    set(
      `sky-${key}-quinary-dark`,
      `color-mix(in oklch, var(--sky-${key}-primary) 28%, var(--sky-text-dark))`,
    );

    set(
      `sky-${key}-g0`,
      `color-mix(in oklch, var(--sky-${key}-primary) 12%, transparent)`,
    );

    set(
      `sky-${key}-g1`,
      `color-mix(in oklch, var(--sky-${key}-primary) 24%, transparent)`,
    );

    set(
      `sky-${key}-g2`,
      `color-mix(in oklch, var(--sky-${key}-primary) 48%, transparent)`,
    );
  });

  /* semantic hover tokens */

  semanticColors.forEach((key) => {
    set(
      `sky-${key}-primary-hover`,
      `color-mix(in oklch, var(--sky-${key}-primary) 86%, var(--sky-text-dark))`,
    );

    set(
      `sky-${key}-secondary-hover`,
      `color-mix(in oklch, var(--sky-${key}-secondary) 86%, var(--sky-text-dark))`,
    );

    set(
      `sky-${key}-tertiary-hover`,
      `color-mix(in oklch, var(--sky-${key}-tertiary) 86%, var(--sky-text-dark))`,
    );

    set(
      `sky-${key}-quaternary-hover`,
      `color-mix(in oklch, var(--sky-${key}-quaternary) 86%, var(--sky-text-dark))`,
    );

    set(
      `sky-${key}-quinary-hover`,
      `color-mix(in oklch, var(--sky-${key}-quinary) 86%, var(--sky-text-dark))`,
    );
  });

  /* ring tokens */

  semanticColors.forEach((key) => {
    set(`sky-ring-${key}-1`, `0 0 0 1px var(--sky-${key}-primary)`);
    set(`sky-ring-${key}-2`, `0 0 0 2px var(--sky-${key}-primary)`);
    set(`sky-ring-${key}-3`, `0 0 0 3px var(--sky-${key}-primary)`);
    set(`sky-ring-${key}-4`, `0 0 0 4px var(--sky-${key}-primary)`);
    set(`sky-ring-${key}-5`, `0 0 0 5px var(--sky-${key}-primary)`);

    set(
      `sky-ring-${key}-inset-1`,
      `inset 0 0 0 1px var(--sky-${key}-primary)`,
    );

    set(
      `sky-ring-${key}-inset-2`,
      `inset 0 0 0 2px var(--sky-${key}-primary)`,
    );

    set(
      `sky-ring-${key}-inset-3`,
      `inset 0 0 0 3px var(--sky-${key}-primary)`,
    );
  });

  // Focus ring: nested var() resolves on this host, then inherits substituted.
  // Components that set `--sky-focus-ring-color` must also redeclare `--sky-focus-ring`
  // (see `helper/styles/focus-ring.ts`).
  set("sky-focus-ring-color", "var(--sky-active-primary)");
  set(
    "sky-focus-ring",
    "0 0 0 3px color-mix(in oklch, var(--sky-focus-ring-color, var(--sky-active-primary)) 42%, transparent)",
  );

  set("sky-focus-ring-offset", "2px");

  set("sky-text-white", "oklch(1 0 0)");
  set("sky-text-black", "oklch(0 0 0)");

  /* on-color fallbacks (solid fills; prefer CSS contrast-color() when supported) */
  set("sky-on-color-fallback-light", "var(--sky-text-white)");
  set("sky-on-color-fallback-dark", "var(--sky-text-black)");

  // Static no-@supports fallbacks (assume dark fill → light FG). Components that
  // paint solid fills should enhance with contrast-color(var(--_fill)).
  set("sky-on-active-primary", "var(--sky-on-color-fallback-light)");
  set("sky-on-success-primary", "var(--sky-on-color-fallback-light)");
  set("sky-on-warning-primary", "var(--sky-on-color-fallback-light)");
  set("sky-on-danger-primary", "var(--sky-on-color-fallback-light)");
  set("sky-on-info-primary", "var(--sky-on-color-fallback-light)");

  /* typography scale tokens */

  Object.entries(theme.fontSize ?? {}).forEach(([k, v]) => {
    set(`sky-font-size-${k}`, v);
  });

  Object.entries(theme.fontWeight ?? {}).forEach(([k, v]) => {
    set(`sky-font-weight-${k}`, v);
  });

  Object.entries(theme.lineHeight ?? {}).forEach(([k, v]) => {
    set(`sky-line-height-${k}`, v);
  });

  Object.entries(theme.letterSpacing ?? {}).forEach(([k, v]) => {
    set(`sky-letter-spacing-${k}`, v);
  });

  /* font shorthand presets */

  const getFontSize = (key: string, fallback: string) =>
    (theme.fontSize as Record<string, string> | undefined)?.[key] ?? fallback;

  const getLineHeight = (key: string, fallback: string) =>
    (theme.lineHeight as Record<string, string> | undefined)?.[key] ??
    fallback;

  const fontPresets: Array<{ key: string; size: string; line: string }> = [
    { key: "xs", size: "xs", line: "tight" },
    { key: "sm", size: "sm", line: "snug" },
    { key: "md", size: "md", line: "normal" },
    { key: "lg", size: "lg", line: "snug" },
    { key: "xl", size: "xl", line: "snug" },
    { key: "2xl", size: "2xl", line: "tight" },
    { key: "3xl", size: "3xl", line: "tight" },
    { key: "4xl", size: "4xl", line: "tight" },
  ];

  for (const { key, size, line } of fontPresets) {
    const sizeVal = getFontSize(size, "1rem");
    const lineVal = getLineHeight(line, "1.5");

    set(`sky-font-${key}`, `${sizeVal} / ${lineVal} var(--sky-font-sans)`);
    set(`sky-font-mono-${key}`, `${sizeVal} / ${lineVal} var(--sky-font-mono)`);
  }

  /* spacing tokens */

  Object.entries(theme.spacing ?? {}).forEach(([k, v]) => {
    set(`sky-space-${k}`, v);
  });

  /* semantic typography role tokens */

  const roleMap = {
    body: { size: "md", weight: "normal", line: "normal" },
    label: { size: "sm", weight: "medium", line: "snug" },
    caption: { size: "xs", weight: "normal", line: "tight" },
    title: { size: "lg", weight: "semibold", line: "snug" },
    heading: { size: "2xl", weight: "bold", line: "tight" },
    display: { size: "4xl", weight: "bold", line: "tight" },
  } as const;

  const getFontWeight = (key: string, fallback: string) =>
    (theme.fontWeight as Record<string, string> | undefined)?.[key] ??
    fallback;

  for (const [role, cfg] of Object.entries(roleMap)) {
    const sizeVal = getFontSize(cfg.size, "1rem");
    const weightVal = getFontWeight(cfg.weight, "400");
    const lineVal = getLineHeight(cfg.line, "1.5");

    set(
      `sky-text-${role}`,
      `${weightVal} ${sizeVal}/${lineVal} var(--sky-font-sans)`,
    );
  }

  return out;
}

/** Expand comma-separated selectors so pseudo-elements attach to each branch. */
function withPseudoSelectors(selector: string, pseudo: string): string {
  return selector
    .split(",")
    .map((part) => `${part.trim()}${pseudo}`)
    .join(",\n");
}

/** Tints native scrollbars from `--sky-scrollbar-*` tokens; width/thickness stay OS-native. */
function buildNativeScrollbarCSS(selector: string): string {
  const webkitTrack = withPseudoSelectors(selector, "::-webkit-scrollbar-track");
  const webkitThumb = withPseudoSelectors(selector, "::-webkit-scrollbar-thumb");
  const webkitThumbHover = withPseudoSelectors(
    selector,
    "::-webkit-scrollbar-thumb:hover",
  );

  return `
${selector} {
  scrollbar-color: var(--sky-scrollbar-thumb) var(--sky-scrollbar-track);
}
${webkitTrack} {
  background: var(--sky-scrollbar-track);
}
${webkitThumb} {
  background-color: var(--sky-scrollbar-thumb);
}
${webkitThumbHover} {
  background-color: var(--sky-scrollbar-thumb-hover);
}
`.trim();
}

const NATIVE_SCROLLBAR_ROOT_SELECTOR = "html, body, *";
const NATIVE_SCROLLBAR_HOST_SELECTOR =
  "sky-theme-provider[sky-native-scrollbars], sky-theme-provider[sky-native-scrollbars] *";

/* buildVarCSS keeps same semantics */
function buildVarCSS(vars: Record<string, string>, selector: string) {
  // Custom-property names containing "." (e.g. --sky-space-2.5) are invalid CSS
  // idents unless the dot is escaped. Without this, fractional spacing tokens are
  // dropped by the parser and never defined. Escape the dot in the name only.
  const body = Object.entries(vars)
    .map(([k, v]) => `${k.replace(/\./g, "\\.")}:${v};`)
    .join("");
  return `${selector}{${body}}`;
}

function ensureStyleTag(id: string, cssText: string): HTMLStyleElement {
  let tag = document.getElementById(id) as HTMLStyleElement | null;
  if (!tag) {
    tag = document.createElement("style");
    tag.id = id;
    document.head.appendChild(tag);
  }
  tag.textContent = cssText;
  return tag;
}

/* context + provider implementation (unchanged except for token emission) */
export interface ThemeContextValue {
  mode: "light" | "dark";
  preference: ThemePreference;
  theme: ThemeConfig;
  setPreference: (pref: ThemePreference) => void;
  togglePreference: () => ThemePreference;
}
export const themeContext = createContext<ThemeContextValue>("sky/theme");
type ThemeScope = "root" | "host" | "both";
export type SkyThemeProviderScope = ThemeScope;
export type SkyThemeProviderGlobalChangeDetail = {
  mode: "light" | "dark";
  preference: ThemePreference;
};


/**
 * @element sky-theme-provider
 *
 * @summary Theme provider for light, dark, and system preferences with CSS variable generation.
 *
 * @status stable
 * @since 1.0.0
 *
 * @documentation https://sky-ui.com/components/theme-provider
 *
 * @slot - Default slot for content that will receive theme context.
 *
 * @csspart container - The theme provider container element.
 *
 * @property {Partial<ThemeConfig>} userTheme - Custom theme overrides.
 * @property {SkyThemeProviderScope} scope - Where CSS variables are applied.
 * @property {"light"|"dark"|"system"} preference - Theme preference.
 * @property {boolean} persist - Persists preference in localStorage.
 * @property {boolean} broadcast - Broadcasts theme changes through events.
 *
 * @fires {CustomEvent<SkyThemeProviderGlobalChangeDetail>} theme-change-global - Fired when theme changes globally.
 * @fires {CustomEvent<SkyThemeProviderGlobalChangeDetail>} sky-theme - Fired on global theme updates.
 *
 * @Behavior
 * - Resolves light/dark mode from explicit or system preference.
 * - Emits CSS custom properties for color, typography, spacing, and motion tokens.
 * - Provides theme context for descendant components.
 *
 * @example
 * ```html
 * <sky-theme-provider preference="system" scope="both">
 *   <app-content></app-content>
 * </sky-theme-provider>
 * ```
 * ```vue
 * <template>
 *   <sky-theme-provider :user-theme="theme" preference="dark" scope="both">
 *     <app-content />
 *   </sky-theme-provider>
 * </template>
 * ```
 * ```jsx
 * export default function Demo() {
 *   return <sky-theme-provider userTheme={theme} preference="dark" scope="both"><app-content /></sky-theme-provider>;
 * }
 * ```
 */
@customElement("sky-theme-provider")
export class SkyThemeProvider extends LitElement {
  /** User theme overrides. From HTML pass JSON, e.g. usertheme='{"primary":"#0af"}'. */
  @property({ type: Object }) userTheme?: PartialTheme;

  /** Where to apply CSS vars and global CSS. Default: "root". */
  @property({ type: String, reflect: true }) scope: ThemeScope = "root";

  @property({ type: String, reflect: true })
  preference: ThemePreference = getThemePreference();

  @state() private mode: "light" | "dark" = "light";

  @provide({ context: themeContext })
  private _ctx!: ThemeContextValue;

  @property({ type: Boolean }) persist: boolean = true;
  @property({ type: Boolean }) broadcast: boolean = true;

  private media: MediaQueryList | null = null;
  private globalStyleId = "sky-theme-provider-global-style";
  private hostScrollbarStyleId = "sky-theme-provider-host-scrollbar";
  private hostSheet: CSSStyleSheet | null = null;
  private hostStyleEl: HTMLStyleElement | null = null;
  private rootVarsStyleId = "sky-theme-vars-root";

  static styles = css`
    :host {
      display: contents;
    }

    :host {
      font-family: var(--sky-font);
      color: var(--sky-text-primary);
      background: var(--sky-background-color);
    }
  `;

  connectedCallback(): void {
    super.connectedCallback();
    this.mode = resolveActualTheme(this.preference);
    this.pushContext();
    this.setupSystemWatcher();
    if (this.scope === "host") {
      this.applyVariables();
    }
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    this.media?.removeEventListener("change", this.onSystemChange);

    if (this.hostStyleEl && this.hostStyleEl.parentNode) {
      this.hostStyleEl.parentNode.removeChild(this.hostStyleEl);
      this.hostStyleEl = null;
    }

    if (this.scope === "host") {
      const s = document.getElementById(this.globalStyleId);
      if (s) s.remove();
      const hostScrollbar = document.getElementById(this.hostScrollbarStyleId);
      if (hostScrollbar) hostScrollbar.remove();
    }
  }

  protected firstUpdated(): void {
    if (this.scope === "root" || this.scope === "both") {
      this.applyVariables();
    }
  }

  override updated(changedProperties: Map<string | number | symbol, unknown>): void {
    super.updated(changedProperties);

    // Ensure context is updated after DOM updates
    if (
      changedProperties.has("userTheme") ||
      changedProperties.has("mode") ||
      changedProperties.has("preference") ||
      changedProperties.has("scope")
    ) {
      this.pushContext();
    }
  }

  protected override willUpdate(changed: Map<string, unknown>): void {
    const prefChanged = changed.has("preference");
    const scopeChanged = changed.has("scope");
    const userThemeChanged = changed.has("userTheme");

    let modeChanged = false;

    if (prefChanged || scopeChanged) {
      const nextMode = resolveActualTheme(this.preference);
      if (nextMode !== this.mode) {
        this.mode = nextMode;
        modeChanged = true;
      }
    }

    const hydrated = !!this.hasUpdated;
    const canApplyNow = this.scope === "host" || hydrated;
    if (canApplyNow && (modeChanged || scopeChanged || userThemeChanged)) {
      this.applyVariables();
    }

    if (prefChanged || modeChanged || userThemeChanged || scopeChanged) {
      this.pushContext();

      // Avoid broadcasting before hydration when scope touches root. Host-only providers still broadcast
      // when userTheme / mode / preference updates so consumers (e.g. sky-chart mono palette) can refresh.
      const hostThemeChanged =
        this.scope === "host" &&
        hydrated &&
        (userThemeChanged || prefChanged || modeChanged || scopeChanged);
      if (
        this.broadcast &&
        ((this.scope === "root" || this.scope === "both") || hostThemeChanged)
      ) {
        this.broadcastTheme();
      }
    }
  }

  private setRootThemeAttrs() {
    const de = document.documentElement;
    de.toggleAttribute("data-theme-light", this.mode === "light");
    de.toggleAttribute("data-theme-dark", this.mode === "dark");
    de.setAttribute("data-theme", this.mode);
    (de.style as any).colorScheme = this.mode;
    de.classList.toggle("dark", this.mode === "dark");
  }

  private broadcastTheme() {
    const detail = { mode: this.mode, preference: this.preference };
    window.dispatchEvent(new CustomEvent("sky-theme", { detail }));

    window.dispatchEvent(new CustomEvent("theme-change-global", { detail }));
  }

  public setPreference(pref: ThemePreference) {
    try {
      if (this.persist && (this.scope === "root" || this.scope === "both")) {
        localStorage.setItem("theme", pref);
      }
    } catch { }
    this.preference = pref;

    if (this.broadcast && (this.scope === "root" || this.scope === "both")) {
      this.dispatchEvent(
        new CustomEvent("theme-change-global", {
          bubbles: true,
          composed: true,
        }),
      );
    }
  }

  public togglePreference(): ThemePreference {
    const current = this.preference ?? getThemePreference();
    const next: ThemePreference =
      current === "light" ? "dark" : current === "dark" ? "system" : "light";
    this.setPreference(next);
    return next;
  }

  private onSystemChange = () => {
    if ((this.preference ?? getThemePreference()) === "system") {
      this.mode = resolveActualTheme("system");
      this.applyVariables();
      this.pushContext();
      this.dispatchEvent(
        new CustomEvent("theme-change-global", {
          bubbles: true,
          composed: true,
        }),
      );
    }
  };

  private setupSystemWatcher() {
    if (typeof window === "undefined" || !window.matchMedia) return;
    this.media = window.matchMedia("(prefers-color-scheme: dark)");
    this.media.addEventListener("change", this.onSystemChange);
  }

  /** Ensure global body + dialog + native scrollbar rules when scope touches root. */
  private ensureGlobalStyle() {
    if (typeof document === "undefined") return;
    const cssText = `
      body {
        font-family: var(--sky-font);
        margin: 0;
        padding: 0;
        background: var(--sky-background-color);
        color: var(--sky-text-primary);
      }

      html[data-sky-dialog-open="true"] {
        overflow: hidden !important;
        position: relative;
        height: 100%;
      }

      ${buildNativeScrollbarCSS(NATIVE_SCROLLBAR_ROOT_SELECTOR)}
    `.trim();

    let tag = document.getElementById(
      this.globalStyleId,
    ) as HTMLStyleElement | null;
    if (!tag) {
      tag = document.createElement("style");
      tag.id = this.globalStyleId;
      tag.textContent = cssText;
      document.head.appendChild(tag);
    } else {
      tag.textContent = cssText;
    }
  }

  private applyHostNativeScrollbars(enabled: boolean) {
    if (typeof document === "undefined") return;
    this.toggleAttribute("sky-native-scrollbars", enabled);
    if (!enabled) {
      const tag = document.getElementById(this.hostScrollbarStyleId);
      if (tag) tag.remove();
      return;
    }
    ensureStyleTag(
      this.hostScrollbarStyleId,
      buildNativeScrollbarCSS(NATIVE_SCROLLBAR_HOST_SELECTOR),
    );
  }

  private applyVariables() {
    const full = mergeTheme(this.userTheme || {});
    const modeTheme = full[this.mode];
    const isDark = this.mode === "dark";
    const vars = generateVarMap(modeTheme, isDark);

    this.toggleAttribute("data-theme-light", this.mode === "light");
    this.toggleAttribute("data-theme-dark", this.mode === "dark");

    if (this.scope === "host" || this.scope === "both") {
      const cssText = buildVarCSS(vars, ":host");

      if (
        "adoptedStyleSheets" in Document.prototype &&
        "replaceSync" in CSSStyleSheet.prototype
      ) {
        if (!this.hostSheet) {
          this.hostSheet = new CSSStyleSheet();

          const current =
            (this.renderRoot as ShadowRoot).adoptedStyleSheets ?? [];
          (this.renderRoot as ShadowRoot).adoptedStyleSheets = [
            ...current,
            this.hostSheet,
          ];
        }
        this.hostSheet.replaceSync(cssText);
      } else {
        if (!this.hostStyleEl) {
          this.hostStyleEl = document.createElement("style");
          this.renderRoot.appendChild(this.hostStyleEl);
        }
        this.hostStyleEl.textContent = cssText;
      }

      this.style.removeProperty("color");
      this.style.removeProperty("background");

      this.applyHostNativeScrollbars(true);
    } else {
      this.applyHostNativeScrollbars(false);
    }

    if (this.scope === "root" || this.scope === "both") {
      const cssText = buildVarCSS(vars, ":root");
      ensureStyleTag(this.rootVarsStyleId, cssText);

      this.setRootThemeAttrs();
      this.ensureGlobalStyle();

      if (document.body) {
        document.body.style.removeProperty("color");
        document.body.style.removeProperty("background");
        document.body.style.removeProperty("font-family");
      }

      this.broadcastTheme();
    }
  }

  private pushContext() {
    const mergedTheme = mergeTheme(this.userTheme ?? {});

    this._ctx = {
      mode: this.mode,
      preference: this.preference,
      theme: mergedTheme,
      setPreference: (pref: ThemePreference) => this.setPreference(pref),
      togglePreference: () => this.togglePreference(),
    };
  }

  override render() {
    return html`<slot></slot>`;
  }
}
